import { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/common/Layout/Layout';
import { API_URL } from '../../config/api';
import { BRAND } from '../../config/brand';
import './Cart.css';

const SITE_URL   = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://theluviajewels.com';
const BRAND_NAME = BRAND?.name || 'Luvia';
const CART_RECOVER_URL = `${SITE_URL}/cart/recover`;

function getSessionId() {
  let id = localStorage.getItem('cart_session_id');
  if (!id) {
    id = `guest_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    localStorage.setItem('cart_session_id', id);
  }
  return id;
}

function buildHeaders(token) {
  const h = {
    'Content-Type': 'application/json',
    'x-session-id': getSessionId(),
  };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

export default function CartRecover() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { updateCartFromResponse, openCart } = useCart();
  const { token: authToken } = useAuth();
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'

  useEffect(() => {
    if (!token || token.trim() === '') {
      setStatus('error');
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`${API_URL}/cart/recover/${encodeURIComponent(token)}`, {
          method: 'GET',
          headers: buildHeaders(authToken),
        });
        const data = await res.json().catch(() => ({}));

        if (cancelled) return;

        if (res.ok && data.success && data.data) {
          updateCartFromResponse(data.data);
          setStatus('success');
          openCart();
          navigate('/cart', { replace: true });
          return;
        }

        setStatus('error');
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();

    return () => { cancelled = true; };
  }, [token, authToken, updateCartFromResponse, openCart, navigate]);

  if (status === 'loading') {
    return (
      <Layout>
        <Helmet>
          <title>Recover Cart | {BRAND_NAME}</title>
          <meta name="description" content="Restoring your cart…" />
          <meta name="robots" content="noindex, follow" />
          <link rel="canonical" href={CART_RECOVER_URL} />
        </Helmet>
        <div className="cart-recover cart-recover--loading">
          <p>Recovering your cart…</p>
        </div>
      </Layout>
    );
  }

  if (status === 'error') {
    return (
      <Layout>
        <Helmet>
          <title>Recovery link expired | {BRAND_NAME}</title>
          <meta name="description" content="This cart recovery link has expired or is no longer valid." />
          <meta name="robots" content="noindex, follow" />
          <link rel="canonical" href={CART_RECOVER_URL} />
        </Helmet>
        <div className="cart-recover cart-recover--error">
          <h1>Link expired or invalid</h1>
          <p>This recovery link has expired or the cart is no longer available.</p>
          <Link to="/shop" className="cart-recover__link">Continue shopping</Link>
        </div>
      </Layout>
    );
  }

  return null;
}
